import React, { useState, useEffect, useMemo } from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { UnderlineTabs } from '@/components/ui/underline-tabs'
import PageHeader from '@/components/PageHeader'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useAuthStore } from '@/stores/authStore'
import { useCompositionStore } from '@/stores/compositionStore'
import { useToast } from '@/hooks/use-toast'
import { useDataFetcher } from '@/hooks/useApiManager'
import { Printer, Loader2 } from 'lucide-react'
import { formatDate, formatNumberSmart } from '@/lib/utils'
import { LineChart, Line, YAxis, Tooltip, ResponsiveContainer } from 'recharts'

const TABS = [
  { id: 'insights', label: 'Visual insights' },
  { id: 'history', label: 'History & logs' },
]

// Recharts strokes are SVG attributes, which cannot use var(); resolve the (possibly brand) primary colour once.
const resolvePrimaryColor = () => {
  if (typeof document === 'undefined') return '#2563EB'
  const value = getComputedStyle(document.documentElement).getPropertyValue('--primary').trim()
  return value ? `hsl(${value})` : '#2563EB'
}

const hasValue = (value) => value !== null && value !== undefined && value !== ''

/** Weight / body fat / lean muscle trend: latest value, change since the first entry, sparkline. */
const TrendCard = ({ label, unit, dataKey, data, color }) => {
  const points = data.filter((row) => hasValue(row[dataKey]) && Number.isFinite(Number(row[dataKey])))
  const first = points[0]
  const last = points[points.length - 1]
  const delta = first && last ? Number(last[dataKey]) - Number(first[dataKey]) : null
  const format = (value) => formatNumberSmart(value, 2)

  return (
    <Card className="p-4">
      <p className="text-sm text-slate-500">{label}</p>
      <p className="mt-1 text-[1.75rem] font-semibold leading-tight tracking-[-0.02em] text-slate-900">
        {last ? format(last[dataKey]) : '--'}
        {last && <span className="ml-1 text-base font-medium text-slate-500">{unit}</span>}
      </p>
      <p className="mt-0.5 text-xs text-slate-500">
        {delta === null || points.length < 2
          ? 'Not enough entries yet'
          : `${delta > 0 ? '+' : delta < 0 ? '−' : ''}${format(Math.abs(delta))} ${unit} since start`}
      </p>

      <div className="mt-4 h-[80px]">
        {points.length > 1 && (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={points} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
              <YAxis hide domain={['dataMin', 'dataMax']} />
              <Tooltip
                cursor={{ stroke: '#E2E8F0' }}
                contentStyle={{ borderRadius: 10, border: '1px solid #E2E8F0', boxShadow: 'none', fontSize: 12 }}
                labelFormatter={(date) => formatDate(date)}
                formatter={(value) => [`${format(value)} ${unit}`, label]}
              />
              <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} dot={false} activeDot={{ r: 3 }} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
      <div className="mt-2 flex justify-between text-[11px] text-slate-500">
        <span>{first ? formatDate(first.date) : ''}</span>
        <span>{last && last !== first ? formatDate(last.date) : ''}</span>
      </div>
    </Card>
  )
}

/** History table: Date column plus measurement columns, hairline rows. */
const HistoryTable = ({ title, headers, data, keys, units, decimals = 2 }) => (
  <Card className="overflow-hidden">
    <div className="border-b border-slate-200 px-4 py-3">
      <h2 className="text-[15px] font-semibold text-slate-900">{title}</h2>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-slate-200 bg-background">
          <tr>
            <th className="h-10 px-4 text-xs font-semibold text-slate-500">Date</th>
            {headers.map((header) => (
              <th key={header} className="h-10 px-4 text-xs font-semibold text-slate-500">{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length > 0 ? (
            data.slice().reverse().map((item, i) => (
              <tr key={i} className="border-b border-slate-100 last:border-b-0 hover:bg-background">
                <td className="whitespace-nowrap px-4 py-3 font-medium text-slate-900">{formatDate(item.date)}</td>
                {keys.map((key, j) => (
                  <td key={key} className="whitespace-nowrap px-4 py-3 text-slate-900">
                    {formatNumberSmart(item[key], decimals) ?? '—'}
                    <span className="ml-1 text-xs text-slate-500">{units[j]}</span>
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={headers.length + 1} className="px-4 py-10 text-center text-sm text-slate-500">No measurements recorded yet</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </Card>
)

const ClientComposition = () => {
  const { companyUrl } = useAuthStore()
  const {
    programs,
    selectedProgram,
    programGoal,
    weights,
    compositions,
    isLoadingPrograms,
    isLoadingCompositions,
    error,
    fetchClientPrograms,
    selectProgram,
    getWeightProgress,
    getCompositionProgress,
    clearError
  } = useCompositionStore()

  const { toast } = useToast()
  const [activeTab, setActiveTab] = useState('insights')
  const [hasInitialized, setHasInitialized] = useState(false)
  const primaryColor = useMemo(resolvePrimaryColor, [])

  // Fetch data
  useDataFetcher(
    async (signal) => {
      if (!hasInitialized && !isLoadingPrograms) {
        setHasInitialized(true)
        return await fetchClientPrograms(companyUrl)
      }
    },
    [companyUrl, fetchClientPrograms, hasInitialized, isLoadingPrograms],
    {
      enabled: !!companyUrl,
      requestKey: `page:fetchClientPrograms:${companyUrl}`
    }
  )

  useEffect(() => {
    if (error) {
      toast({ title: "Error", description: error, variant: "destructive" })
      clearError()
    }
  }, [error, toast, clearError])

  const handleProgramChange = async (programId) => {
    const program = programs.find((p) => String(p.id) === programId)
    if (companyUrl && program) await selectProgram(companyUrl, program)
  }

  const weightProgress = useMemo(() => getWeightProgress(), [getWeightProgress, weights])
  const compositionProgress = useMemo(() => {
    const progress = getCompositionProgress()
    // Based on comparison with old app, Forearm matches Arm value
    return progress.map(item => ({
      ...item,
      forearm: item.arm
    }))
  }, [getCompositionProgress, compositions])

  const handlePrint = () => window.print()

  const programLabel = (program) => `${safeGet(program, 'name', 'Session')} (${safeGet(program, 'goal.name', 'Program')})`
  const subtitle = programGoal
    ? [safeGet(selectedProgram, 'goal.name'), `since ${formatDate(programGoal.program_availability.program_startdate)}`].filter(Boolean).join(' · ')
    : undefined

  return (
    <div className="app-page">
      <PageHeader title="Body composition" subtitle={subtitle}>
        {programs?.length > 0 && (
          <Select value={selectedProgram ? String(selectedProgram.id) : ''} onValueChange={handleProgramChange}>
            <SelectTrigger className="h-9 w-full text-sm sm:w-[270px]">
              <SelectValue placeholder="Select program" />
            </SelectTrigger>
            <SelectContent>
              {programs.map((program) => (
                <SelectItem key={program.id} value={String(program.id)}>{programLabel(program)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Button variant="outline" size="sm" onClick={handlePrint}>
          <Printer className="mr-1.5 h-4 w-4" />
          Print
        </Button>
      </PageHeader>

      {isLoadingPrograms || isLoadingCompositions ? (
        <div className="flex items-center justify-center gap-3 py-24 text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-sm">Loading your measurements…</span>
        </div>
      ) : (
        <>
          <UnderlineTabs tabs={TABS} value={activeTab} onChange={setActiveTab} label="Body composition views" />

          {activeTab === 'insights' ? (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <TrendCard label="Weight" unit="lbs" dataKey="weight" data={weightProgress} color={primaryColor} />
              <TrendCard label="Body fat" unit="%" dataKey="bodyfat" data={weightProgress} color={primaryColor} />
              <TrendCard label="Muscle mass" unit="lbs" dataKey="leanmuscle" data={weightProgress} color={primaryColor} />
            </div>
          ) : (
            <div className="space-y-4">
              <HistoryTable
                title="Weight & mass history"
                headers={['Weight', 'Muscle', 'Water', 'Fat']}
                data={weightProgress}
                keys={['weight', 'leanmuscle', 'waterweight', 'fatmass']}
                units={['lbs', 'lbs', 'lbs', 'lbs']}
              />
              <HistoryTable
                title="Body composition trends"
                headers={['Body fat %']}
                data={weightProgress}
                keys={['bodyfat']}
                units={['%']}
              />
              <HistoryTable
                title="Circumference details"
                headers={['Neck', 'Arm', 'Chest', 'Waist', 'Hip', 'Thigh', 'Calf', 'Forearm']}
                data={compositionProgress}
                keys={['neck', 'arm', 'chest', 'waist', 'hip', 'thigh', 'calf', 'forearm']}
                units={['in', 'in', 'in', 'in', 'in', 'in', 'in', 'in']}
                decimals={1}
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}

// Simple helper for object path safety
const safeGet = (obj, path, defaultValue = null) => {
  try {
    return path.split('.').reduce((current, key) => current?.[key], obj) ?? defaultValue
  } catch {
    return defaultValue
  }
}

export default ClientComposition
